import { X, CreditCard } from "lucide-react";
import { useState } from "react";
import type { PaymentCard } from "./payment-card-item";

interface AddCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (card: Omit<PaymentCard, "id">) => void;
  editingCard?: PaymentCard | null;
}

export function AddCardModal({
  isOpen,
  onClose,
  onSave,
  editingCard,
}: AddCardModalProps) {
  const [cardNumber, setCardNumber] = useState(
    editingCard ? `************${editingCard.lastFourDigits}` : ""
  );
  const [cardholderName, setCardholderName] = useState(
    editingCard?.cardholderName || ""
  );
  const [expiryDate, setExpiryDate] = useState(editingCard?.expiryDate || "");
  const [cvv, setCvv] = useState("");
  const [cardType, setCardType] = useState<"credit" | "debit">(
    editingCard?.type || "credit"
  );
  const [isDefault, setIsDefault] = useState(editingCard?.isDefault || false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!cardNumber || !cardholderName || !expiryDate || !cvv) {
      alert("Por favor, preencha todos os campos");
      return;
    }

    // Get last 4 digits
    const lastFourDigits = cardNumber.replace(/\s/g, "").slice(-4);

    // Detect card brand (simplified)
    let brand: PaymentCard["brand"] = "visa";
    const firstDigit = cardNumber.replace(/\s/g, "")[0];
    if (firstDigit === "4") brand = "visa";
    else if (firstDigit === "5") brand = "mastercard";
    else if (firstDigit === "3") brand = "amex";
    else if (firstDigit === "6") brand = "elo";

    onSave({
      type: cardType,
      brand,
      lastFourDigits,
      cardholderName: cardholderName.toUpperCase(),
      expiryDate,
      isDefault,
    });

    // Reset form
    setCardNumber("");
    setCardholderName("");
    setExpiryDate("");
    setCvv("");
    setCardType("credit");
    setIsDefault(false);
    onClose();
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, "");
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(" ") : cleaned;
  };

  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return cleaned;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div
        className="fixed inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-card w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-lg font-medium">
              {editingCard ? "Editar Cartão" : "Adicionar Cartão"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Card Type */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Tipo de Cartão
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setCardType("credit")}
                className={`flex-1 py-3 rounded-xl border-2 transition-colors ${
                  cardType === "credit"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <span className="font-medium">Crédito</span>
              </button>
              <button
                type="button"
                onClick={() => setCardType("debit")}
                className={`flex-1 py-3 rounded-xl border-2 transition-colors ${
                  cardType === "debit"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <span className="font-medium">Débito</span>
              </button>
            </div>
          </div>

          {/* Card Number */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Número do Cartão
            </label>
            <input
              type="text"
              value={cardNumber}
              onChange={(e) =>
                setCardNumber(formatCardNumber(e.target.value.slice(0, 19)))
              }
              placeholder="0000 0000 0000 0000"
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              maxLength={19}
              disabled={!!editingCard}
            />
          </div>

          {/* Cardholder Name */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Nome no Cartão
            </label>
            <input
              type="text"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value.toUpperCase())}
              placeholder="NOME COMPLETO"
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary uppercase"
            />
          </div>

          {/* Expiry Date and CVV */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Validade</label>
              <input
                type="text"
                value={expiryDate}
                onChange={(e) =>
                  setExpiryDate(formatExpiryDate(e.target.value.slice(0, 5)))
                }
                placeholder="MM/AA"
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={5}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">CVV</label>
              <input
                type="text"
                value={cvv}
                onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="123"
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={4}
              />
            </div>
          </div>

          {/* Set as Default */}
          <div className="flex items-center justify-between p-4 bg-accent rounded-xl">
            <span className="text-sm font-medium">Tornar cartão padrão</span>
            <button
              type="button"
              onClick={() => setIsDefault(!isDefault)}
              className={`w-12 h-6 rounded-full transition-colors ${
                isDefault ? "bg-primary" : "bg-gray-300"
              }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                  isDefault ? "translate-x-6" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-3 rounded-xl font-medium transition-colors"
          >
            {editingCard ? "Salvar Alterações" : "Adicionar Cartão"}
          </button>
        </form>
      </div>
    </div>
  );
}
